import { useEffect, useRef, useState } from "react";
import { UploadCloud, FileText, Download, Trash2, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";

const CATEGORIES = [
    "Credit Report",
    "Government ID",
    "Proof of Address",
    "Dispute Letter",
    "Creditor Correspondence",
    "Other",
];

function bytes(n) {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function extLabel(name) {
    const dot = name.lastIndexOf(".");
    return dot >= 0 ? name.slice(dot + 1).toUpperCase() : "FILE";
}

export default function DocumentsPanel({ endpoint = "/portal/documents", downloadBase = "/portal/documents" }) {
    const [docs, setDocs] = useState(null);
    const [busy, setBusy] = useState(false);
    const [drag, setDrag] = useState(false);
    const [category, setCategory] = useState("Credit Report");
    const [note, setNote] = useState("");
    const fileRef = useRef(null);
    const backend = process.env.REACT_APP_BACKEND_URL;

    const load = async () => {
        try {
            const { data } = await api.get(endpoint);
            setDocs(data);
        } catch (e) {
            setDocs([]);
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [endpoint]);

    const doUpload = async (file) => {
        if (!file) return;
        const form = new FormData();
        form.append("file", file);
        form.append("category", category);
        if (note) form.append("note", note);
        setBusy(true);
        try {
            await api.post("/portal/documents", form, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(`${file.name} uploaded securely.`);
            setNote("");
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDrag(false);
        const f = e.dataTransfer.files?.[0];
        if (f) doUpload(f);
    };

    const onDelete = async (id) => {
        if (!window.confirm("Delete this document? This cannot be undone.")) return;
        try {
            await api.delete(`/portal/documents/${id}`);
            toast.success("Document deleted.");
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    return (
        <div className="border hairline bg-card p-6 md:p-8" data-testid="documents-panel">
            <div className="flex items-baseline justify-between mb-6 flex-wrap gap-4">
                <div>
                    <div className="text-[10px] tracking-[0.24em] uppercase text-muted-foreground">
                        Secure Vault
                    </div>
                    <div className="font-editorial text-3xl md:text-4xl tracking-tighter">
                        Your documents
                    </div>
                </div>
                <div className="flex items-center gap-2 font-mono-ui text-[10px] tracking-[0.18em] uppercase text-muted-foreground">
                    <Lock size={12} /> encrypted transport · private to your file
                </div>
            </div>

            {/* Dropzone */}
            <div
                onDragOver={(e) => {
                    e.preventDefault();
                    setDrag(true);
                }}
                onDragLeave={() => setDrag(false)}
                onDrop={onDrop}
                className={`grid grid-cols-12 gap-4 border hairline p-6 md:p-8 transition-colors ${
                    drag ? "bg-secondary border-foreground" : "bg-background"
                }`}
                data-testid="upload-dropzone"
            >
                <div className="col-span-12 md:col-span-7 flex items-start gap-4">
                    <div className="w-12 h-12 border hairline grid place-items-center shrink-0">
                        <UploadCloud size={20} strokeWidth={1.5} />
                    </div>
                    <div>
                        <div className="font-editorial text-2xl tracking-tight">
                            Drag &amp; drop a file — or select one below.
                        </div>
                        <div className="mt-2 text-xs font-mono-ui text-muted-foreground">
                            PDF, images, DOC, XLS, CSV, TXT, ZIP · max 25 MB per file.
                        </div>
                    </div>
                </div>

                <div className="col-span-12 md:col-span-5 space-y-3">
                    <label className="block">
                        <span className="block text-[10px] tracking-[0.22em] uppercase text-muted-foreground mb-2">
                            Category
                        </span>
                        <select
                            data-testid="upload-category"
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                            className="w-full bg-transparent border hairline focus:border-foreground outline-none py-2 px-3 font-mono-ui text-sm"
                        >
                            {CATEGORIES.map((c) => (
                                <option key={c} value={c}>
                                    {c}
                                </option>
                            ))}
                        </select>
                    </label>
                    <label className="block">
                        <span className="block text-[10px] tracking-[0.22em] uppercase text-muted-foreground mb-2">
                            Note (optional)
                        </span>
                        <input
                            data-testid="upload-note"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="e.g. Experian report, June 2025"
                            className="w-full bg-transparent border hairline focus:border-foreground outline-none py-2 px-3 font-mono-ui text-sm"
                        />
                    </label>
                    <div className="flex items-center gap-3">
                        <input
                            ref={fileRef}
                            type="file"
                            className="hidden"
                            data-testid="upload-input"
                            onChange={(e) => doUpload(e.target.files?.[0])}
                        />
                        <button
                            type="button"
                            data-testid="upload-select-btn"
                            disabled={busy}
                            onClick={() => fileRef.current?.click()}
                            className="inline-flex items-center gap-2 bg-foreground text-background px-4 py-3 text-[11px] tracking-[0.18em] uppercase hover:bg-accent disabled:opacity-60"
                        >
                            {busy ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />}
                            {busy ? "Uploading…" : "Select file"}
                        </button>
                    </div>
                </div>
            </div>

            {/* List */}
            <div className="mt-6" data-testid="documents-list">
                {docs === null && (
                    <div className="text-sm font-mono-ui text-muted-foreground py-6">Loading…</div>
                )}
                {docs && docs.length === 0 && (
                    <div className="border hairline py-10 text-center">
                        <FileText size={22} strokeWidth={1.5} className="inline-block text-muted-foreground" />
                        <div className="mt-3 font-editorial text-xl">No documents yet.</div>
                        <div className="mt-1 text-xs font-mono-ui text-muted-foreground">
                            Upload your credit report to get started.
                        </div>
                    </div>
                )}
                {docs && docs.length > 0 && (
                    <ul className="divide-y hairline border-t border-b hairline">
                        {docs.map((d) => (
                            <li
                                key={d.id}
                                data-testid={`doc-row-${d.id}`}
                                className="grid grid-cols-12 gap-4 items-center py-4"
                            >
                                <div className="col-span-1">
                                    <div className="w-10 h-10 border hairline grid place-items-center font-mono-ui text-[10px] tracking-widest">
                                        {extLabel(d.original_name).slice(0, 4)}
                                    </div>
                                </div>
                                <div className="col-span-6 md:col-span-5">
                                    <div className="font-editorial text-lg leading-tight truncate">
                                        {d.original_name}
                                    </div>
                                    {d.note && (
                                        <div className="text-xs text-muted-foreground truncate">{d.note}</div>
                                    )}
                                </div>
                                <div className="col-span-3 md:col-span-2 font-mono-ui text-[10px] tracking-[0.18em] uppercase text-muted-foreground">
                                    {d.category}
                                </div>
                                <div className="hidden md:block col-span-2 font-mono-ui text-xs text-muted-foreground">
                                    {bytes(d.size)}
                                </div>
                                <div className="col-span-2 flex items-center gap-2 justify-end">
                                    <a
                                        href={`${backend}/api${downloadBase}/${d.id}/download`}
                                        target="_blank"
                                        rel="noreferrer"
                                        data-testid={`doc-download-${d.id}`}
                                        className="inline-flex items-center gap-1 border hairline px-3 py-2 text-[10px] tracking-[0.16em] uppercase hover:bg-foreground hover:text-background"
                                    >
                                        <Download size={12} /> Download
                                    </a>
                                    {endpoint === "/portal/documents" && (
                                        <button
                                            onClick={() => onDelete(d.id)}
                                            data-testid={`doc-delete-${d.id}`}
                                            className="inline-flex items-center border hairline p-2 hover:bg-destructive hover:text-destructive-foreground"
                                            aria-label="Delete"
                                        >
                                            <Trash2 size={12} />
                                        </button>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}
